import { useState, useEffect } from "react";
import { useResponsive } from 'ahooks'
import { DateTime } from 'luxon'
const { findWindows } = await import(
  /* webpackChunkName: "windowsiana" */ 'windows-iana'
);

import AvatarComponent from "./common/avatar"

import CaretIcon from "../../images/caret.svg";
import WalletIcon from "../../images/wallet.svg";
import SettingsIcon from "../../images/settings.svg";
import LogoutIcon from "../../images/logout.svg";
import LogoIcon from "../../images/logo.svg";
import MenuIcon from "../../images/menu.svg";

type Props = LayoutProps & TimezoneDifferenceComponentProps;

export default function Component(props: Props): JSX.Element {
  return (<>
    <LayoutComponent {...props} />
    <TimezoneDifferenceComponent
      currentAttendeeId={props.currentAttendeeId}
      accountUrl={props.settingsUrl}
      userTimezoneName={props.userTimezoneName}
      timezones={props.timezones} />
  </>)
}

type LayoutProps = {
  name: string;
  selectedMenu: string;
  licensesText: string;
  licensesUrl: string;
  tutorialUrl: string;
  outlookUrl: string;
  settingsUrl: string;
  signOutUrl: string;
  userHomeUrl: string;
  meetingsUrl: string;
  userTasksUrl: string;
  teamsUrl: string;
};

function LayoutComponent(props: Props): JSX.Element {
  const responsive = useResponsive()

  if (!responsive["lg"]) {
    return <MobileLayout {...props} />
  }

  return <DesktopLayout {...props} />
}

function MobileLayout({
  name,
  selectedMenu,
  licensesText,
  licensesUrl,
  tutorialUrl,
  settingsUrl,
  signOutUrl,
  userHomeUrl,
  meetingsUrl,
  userTasksUrl,
  teamsUrl,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => setIsOpen(!isOpen);

  useEffect(() => {
    const close = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener("click", close);
    } else {
      document.removeEventListener("click", close);
    }
    return () => document.removeEventListener("click", close);
  }, [isOpen]);

  let links = [
    { text: "Help", href: "https://support.lessmeeting.com", pageName: "help", isSeparated: true },
    { text: "Tutorial", href: tutorialUrl, pageName: "help", isSeparated: true },
    { text: "Dashboard", href: userHomeUrl, pageName: "home", isSeparated: true },
    { text: "Meetings", href: meetingsUrl, pageName: "meetings", isSeparated: false },
    { text: "Action Items", href: userTasksUrl, pageName: "tasks", isSeparated: false },
    { text: "Teams", href: teamsUrl, pageName: "team", isSeparated: false },
    { text: "Settings", href: settingsUrl, pageName: "settings", isSeparated: true },
    { text: "Sign Out", href: signOutUrl, pageName: "signout", isSeparated: true },
  ]

  if (licensesUrl) {
    links = [
      { text: "Help", href: "https://support.lessmeeting.com", pageName: "help", isSeparated: true },
      { text: "Tutorial", href: tutorialUrl, pageName: "help", isSeparated: true },
      { text: "Dashboard", href: userHomeUrl, pageName: "home", isSeparated: true },
      { text: "Meetings", href: meetingsUrl, pageName: "meetings", isSeparated: false },
      { text: "Action Items", href: userTasksUrl, pageName: "tasks", isSeparated: false },
      { text: "Teams", href: teamsUrl, pageName: "team", isSeparated: false },
      { text: licensesText, href: licensesUrl, pageName: "licenses", isSeparated: true },
      { text: "Settings", href: settingsUrl, pageName: "settings", isSeparated: false },
      { text: "Sign Out", href: signOutUrl, pageName: "signout", isSeparated: true },
    ]
  }

  return (
    <>
      <div className="w-full bg-black flex px-4 items-center">
        <MenuIcon className="w-8 text-white mr-auto" onClick={toggleOpen} />
        <a href={userHomeUrl} className="ml-3">
          <LogoIcon className="layout-logo" />
        </a>
      </div>
      {isOpen
        ? <div className="bg-black fixed p-4 max-w-xs h-full z-10">
          <div className="flex items-center pb-2">
            <AvatarComponent name={name} className="mr-6" />
            <div className="text-white truncate" title={name}>
              {name}
            </div>
          </div>
          {links.map(({ text, href, pageName, isSeparated }) => (
            <a
              key={text}
              href={href}
              className="shrink-0">
              <div className={`${selectedMenu == pageName ? "text-secondary" : "text-white"} py-2 ${isSeparated ? "border-t border-white" : ""}`}>
                {text}
              </div>
            </a>))}
        </div>
        : null}
    </>
  );
}

function DesktopLayout({
  name,
  selectedMenu,
  licensesText,
  licensesUrl,
  tutorialUrl,
  settingsUrl,
  signOutUrl,
  userHomeUrl,
  meetingsUrl,
  userTasksUrl,
  teamsUrl,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => setIsOpen(!isOpen);

  useEffect(() => {
    const close = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener("click", close);
    } else {
      document.removeEventListener("click", close);
    }
    return () => document.removeEventListener("click", close);
  }, [isOpen]);

  const links = [
    { text: "Dashboard", href: userHomeUrl, pageName: "home" },
    { text: "Meetings", href: meetingsUrl, pageName: "meetings" },
    { text: "Action Items", href: userTasksUrl, pageName: "tasks" },
    { text: "Teams", href: teamsUrl, pageName: "team" },
  ]

  return (
    <div className="w-full bg-black flex px-12 items-stretch">
      <a href={userHomeUrl}>
        <LogoIcon className="layout-logo" />
      </a>
      <div className="flex mx-auto shrink-0">
        {links.map(({ text, href, pageName }) => (<a key={text} href={href} className="shrink-0"><div className={`${selectedMenu == pageName ? "active" : ""} navigation-tab`}>{text}</div></a>))}
      </div>
      <div className="flex items-center truncate">
        <a href="https://support.lessmeeting.com" target="_blank">
          <div className="p-2 ml-6 bg-gray-800 rounded-xl">
            <div className="size-5 text-xs text-center text-white border border-white rounded-full">
              ?
            </div>
          </div>
        </a>
        <div
          className="flex items-center cursor-pointer truncate"
          onClick={toggleDropdown}
        >
          <AvatarComponent name={name} className="mx-6" />
          <div className="max-w-sm mr-4 text-white truncate" title={name}>
            {name}
          </div>
          <CaretIcon className="text-primary shrink-0" />
        </div>
        {isOpen ? (
          <div className="absolute z-50 bg-white rounded-lg shadow-lg top-16 right-8 w-44">
            <div className="p-2">
              <div className="pb-1 border-b border-gray-400">
                <a href={tutorialUrl}>
                  <div
                    className={`dropdown-item ${selectedMenu == "settings" ? "active" : ""
                      }`}
                  >
                    <SettingsIcon className="mr-4" />
                    <div>Tutorial</div>
                  </div>
                </a>
              </div>
              {licensesUrl ? (
                <div>
                  <a href={licensesUrl}>
                    <div
                      className={`dropdown-item ${selectedMenu == "licenses" ? "active" : ""
                        }`}
                    >
                      <WalletIcon className="mr-4" />
                      <div>{licensesText}</div>
                    </div>
                  </a>
                </div>
              ) : null}
              <div className="pb-1 border-b border-gray-400">
                <a href={settingsUrl}>
                  <div
                    className={`dropdown-item ${selectedMenu == "settings" ? "active" : ""
                      }`}
                  >
                    <SettingsIcon className="mr-4" />
                    <div>Settings</div>
                  </div>
                </a>
              </div>
              <div>
                <a href={signOutUrl}>
                  <div className="dropdown-item">
                    <LogoutIcon className="mr-4" />
                    <div>Sign Out</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

type TimezoneDifferenceComponentProps = {
  currentAttendeeId: string
  userTimezoneName: string
  timezones: {
    value: string
    label: string
  }[]
  accountUrl: string
}

function TimezoneDifferenceComponent({
  currentAttendeeId,
  accountUrl,
  userTimezoneName,
  timezones
}: TimezoneDifferenceComponentProps): JSX.Element {
  const browserTimeZone = timezones.find(t => t.value == findWindows(window.browserTimeZoneName)[0]);

  const currentBrowserWindowsTimezone = browserTimeZone?.label;
  const dismissLocalStorageKey = `${currentAttendeeId} - ${userTimezoneName} - ${currentBrowserWindowsTimezone}`
  let dismissLocalStorageValue = localStorage.getItem(dismissLocalStorageKey)

  const [isOpen, setOpen] = useState<boolean>(DateTime.now().offset !== -new Date().getTimezoneOffset() && dismissLocalStorageValue == null)

  if (!browserTimeZone || !isOpen) {
    return null
  }

  if (dismissLocalStorageValue && DateTime.fromISO(dismissLocalStorageValue).diffNow().shiftTo('days').days < -14) {
    localStorage.removeItem(dismissLocalStorageKey)
    dismissLocalStorageValue = null
  }

  const onClose = () => {
    localStorage.setItem(dismissLocalStorageKey, new Date().toISOString())
    setOpen(false)
  }

  return (
    <div className="w-full bg-amber-100 p-4 lg:px-12 text-sm">
      <p className="mb-1">
        The time zone in your profile is set to {userTimezoneName}, but we've detected a change to {currentBrowserWindowsTimezone}. Would you like to update your working hours to your current time zone?
      </p>
      <div className="flex">
        <a className="text-primary font-medium hover:underline" href={accountUrl}>Configure in settings</a>
        <button className="text-primary font-medium hover:underline ml-4" onClick={onClose}>No, thanks</button>
      </div>
    </div>
  )
}
